import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { PlusIcon, XIcon } from 'lucide-react';
import { useState } from 'react';
import { useChangelogApi } from '../../services/api/useChangelog.api';
import { useChangelogItemTypeApi } from '../../services/api/useChangelogItemType.api';
import { useChangelogStatusApi } from '../../services/api/useChangelogStatus.api';
import { useTeamApi } from '../../services/api/useTeam.api';
import { Changelog } from '../../types/Changelog/Changelog.type';
import { ChangelogCreate, ChangelogItemCreate } from '../../types/Changelog/ChangelogCreate.type';
import ChangelogItemForm from './ChangelogItemForm';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { format, parseISO } from 'date-fns';

interface ChangelogFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (changelog: ChangelogCreate) => void;
  isSubmitting: boolean;
  changelogId?: number;
}

const createEmptyItem = (): ChangelogItemCreate => ({
  title: '',
  description: '',
  type_id: 0,
  team_ids: [],
});

const toDate = (isoDate: string | null): Date | null => (isoDate ? parseISO(isoDate) : null);

const mapInitialItems = (changelog?: Changelog): ChangelogItemCreate[] => {
  if (!changelog || changelog.items.length === 0) return [createEmptyItem()];
  return changelog.items.map((item) => ({
    title: item.title,
    description: item.description,
    type_id: item.type_id,
    team_ids: item.teams?.map((team) => team.id) ?? [],
  }));
};

function ChangelogFormDialog({ open, onClose, onSubmit, isSubmitting, changelogId }: ChangelogFormDialogProps) {
  const { getTeams } = useTeamApi();
  const { getChangelogById } = useChangelogApi();
  const { getChangelogStatuses } = useChangelogStatusApi();
  const { getChangelogItemTypes } = useChangelogItemTypeApi();

  const isEditMode = changelogId != null;
  const actionLabel = isEditMode ? 'Update changelog' : 'Create changelog';

  const [title, setTitle] = useState<string>('');
  const [summary, setSummary] = useState<string>('');
  const [releaseDate, setReleaseDate] = useState<Date | null>(null);
  const [statusId, setStatusId] = useState<number | ''>('');
  const [items, setItems] = useState<ChangelogItemCreate[]>([createEmptyItem()]);
  const [prevOpen, setPrevOpen] = useState(open);
  const [loadedChangelogId, setLoadedChangelogId] = useState<number | undefined>(undefined);

  const { data: teamsResponse } = useQuery({
    queryKey: ['allTeams', 'changelog'],
    queryFn: () => getTeams({ page: 1, per_page: 100, sort: 'name:asc' }),
  });

  const teams = teamsResponse?.data ?? [];

  const { data: statuses = [] } = useQuery({
    queryKey: ['changelogStatuses'],
    queryFn: getChangelogStatuses,
  });

  const { data: itemTypes = [] } = useQuery({
    queryKey: ['changelogItemTypes'],
    queryFn: getChangelogItemTypes,
  });

  const { data: editingChangelog, isLoading: isLoadingChangelog } = useQuery({
    queryKey: ['changelog', changelogId],
    queryFn: () => getChangelogById(changelogId as number),
    enabled: open && isEditMode,
  });

  const applyChangelog = (changelog?: Changelog) => {
    setTitle(changelog?.title ?? '');
    setSummary(changelog?.summary ?? '');
    setReleaseDate(toDate(changelog?.release_date ?? null));
    setStatusId(changelog?.status_id ?? '');
    setItems(mapInitialItems(changelog));
  };

  // reset form on open/close
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setLoadedChangelogId(undefined);
      if (!isEditMode) {
        applyChangelog(undefined);
      }
    }
  }

  // load changelog by id
  if (open && isEditMode && editingChangelog && loadedChangelogId !== changelogId) {
    setLoadedChangelogId(changelogId);
    applyChangelog(editingChangelog);
  }

  const isItemValid = (item: ChangelogItemCreate) => Boolean(item.title && item.description && item.type_id);
  const isFormValid =
    Boolean(title) && statusId !== '' && items.length > 0 && items.every(isItemValid) && releaseDate !== null;

  const handleItemChange = (index: number, item: ChangelogItemCreate) => {
    setItems((prev) => prev.map((existing, i) => (i === index ? item : existing)));
  };

  const handleAddItem = () => {
    setItems((prev) => [...prev, createEmptyItem()]);
  };

  const handleRemoveItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (!isFormValid) return;

    onSubmit({
      title,
      summary: summary || null,
      release_date: releaseDate ? format(releaseDate, 'yyyy-MM-dd') : null,
      status_id: statusId as number,
      items,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      sx={{ '& .MuiDialog-paper': { background: '#F7FAFC' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {actionLabel}
        <IconButton size='small' onClick={onClose} aria-label='Close'>
          <XIcon size={18} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {isEditMode && isLoadingChangelog ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 6,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Stack spacing={2.5} sx={{ paddingTop: 1 }}>
            <TextField
              type='text'
              label='Title'
              variant='outlined'
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              sx={{ '& .MuiInputLabel-root': { fontSize: 14 } }}
            />
            <TextField
              type='text'
              label='Summary'
              variant='outlined'
              multiline
              minRows={2}
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              sx={{ '& .MuiInputLabel-root': { fontSize: 14 } }}
            />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label='Release date'
                value={releaseDate}
                onChange={(newValue) => setReleaseDate(newValue)}
                slotProps={{
                  textField: {
                    slotProps: {
                      input: {
                        sx: {
                          fontSize: '14px',
                        },
                      },
                      inputLabel: {
                        sx: {
                          fontSize: 14,
                        },
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>

            <FormControl>
              <InputLabel id='select-status-label' sx={{ fontSize: '14px' }}>
                Select a status
              </InputLabel>
              <Select
                labelId='select-status-label'
                value={statusId === '' ? '' : statusId.toString()}
                label='Select a status'
                onChange={(e) => setStatusId(Number(e.target.value))}
              >
                {statuses.map((status) => (
                  <MenuItem key={status.id} value={status.id}>
                    {status.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider />

            <Typography sx={{ fontWeight: 500 }}>Items</Typography>

            {items.map((item, index) => (
              <ChangelogItemForm
                key={index}
                item={item}
                index={index}
                teams={teams}
                types={itemTypes}
                onChange={handleItemChange}
                onRemove={handleRemoveItem}
                removable={items.length > 1}
              />
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Button variant='outlined' disableElevation startIcon={<PlusIcon size={16} />} onClick={handleAddItem}>
          Add item
        </Button>
        <Button
          variant='contained'
          color='primary'
          disableElevation
          disabled={!isFormValid || isSubmitting}
          onClick={handleSubmit}
        >
          {actionLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ChangelogFormDialog;
